import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LineChart, Line, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { MagnifyingGlass, ChartLine, ChartBar, Info, Plus, Check, ArrowRight, ArrowSquareOut, Sword, Trophy, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    timingSearchPlayers,
    timingKnownTeams,
    timingPlayerDetail,
    timingPlayerOpponents,
    timingFdrPerfDetail,
} from "../../lib/timing-api";
import { timingPoolMatch, timingPoolMatrix } from "../../lib/timing-pool-api";
import { t, POSITIONS, PERIODS } from "../../lib/timing-i18n";
import { PositionPill, MetricPill, TimingBreakdown, SeasonPicker } from "./TimingBits";
import { useListKeyboardNav } from "../../lib/use-list-keyboard-nav";

// ---------------------------------------------------------------------------
// Full 38-GW fixture schedule for the selected player — reuses the same
// blending logic as the Fixture Matrix (opponent × phase × H/A) but rendered
// as a compact grid so users don't have to bounce over to the Matrix tab.
// ---------------------------------------------------------------------------
function ScheduleCell({ c, playerPpg, mode }) {
    if (c.blank) {
        return (
            <div className="border border-white/10 bg-black/30 p-1.5 text-center opacity-40" title={`GW${c.gw} · Volný GW`}>
                <div className="font-mono text-[8px] uppercase tracking-widest text-white/40">GW{c.gw}</div>
                <div className="font-mono text-[10px] text-white/40">—</div>
            </div>
        );
    }
    const exp = c.expected_ppg;
    // Colour scale is now RELATIVE to THIS player's long-term average
    // (deviation), so a 3.6-PPG player still gets green cells when they
    // beat their own baseline. Avoids "only Haaland is green".
    // Thresholds tuned around real per-player noise (~0.5 PPG spread).
    const dev = c.deviation ?? 0;
    const bg = dev >= 1.0 ? "bg-fa-mint/85 text-black"
             : dev >= 0.5 ? "bg-fa-mint/55 text-black"
             : dev >= 0.15 ? "bg-fa-mint/25 text-fa-mint"
             : dev > -0.15 ? "bg-white/[0.04] text-white/85"
             : dev > -0.5 ? "bg-orange-500/25 text-orange-100"
             : dev > -1.0 ? "bg-orange-500/45 text-orange-100"
             : "bg-red-500/45 text-red-100";
    const devStr = dev > 0 ? `+${dev.toFixed(1)}` : dev.toFixed(1);
    const histNote = c.hist_games > 0
        ? `Historicky: ${c.hist_games} zápasů, ${c.hist_ppg} PPG proti ${c.opponent}`
        : `Bez historických dat proti ${c.opponent} — použit baseline PPG`;
    // Big number shown depends on mode: "ppg" = absolute, "dev" = deviation
    const bigValue = mode === "dev" ? devStr : exp.toFixed(1);
    return (
        <div
            className={`${bg} border border-black/60 p-1.5 text-center relative`}
            title={`GW${c.gw} · ${c.opponent}${c.is_home ? " (doma)" : " (venku)"}\n${histNote}\nOčekávané body: ${exp.toFixed(2)} (odchylka ${devStr} vs dlouhodobý průměr ${playerPpg?.toFixed?.(2) || "?"})`}
            data-testid={`schedule-cell-${c.gw}`}
        >
            <div className="font-mono text-[8px] uppercase tracking-widest opacity-90 flex items-center justify-center gap-1">
                <span className="opacity-70">GW{c.gw}</span>
                <span className={`px-1 font-black border ${c.is_home ? "bg-black/80 text-fa-mint border-fa-mint" : "bg-black/80 text-white/80 border-white/40"}`}>
                    {c.is_home ? "H" : "A"}
                </span>
            </div>
            <div className="font-mono text-[10px] font-black leading-tight tabular-nums mt-0.5">
                {c.opp_short || c.opponent?.slice(0, 3).toUpperCase()}
            </div>
            <div className="font-mono text-[11px] font-black tabular-nums leading-none mt-0.5">
                {bigValue}
            </div>
            {c.hist_games > 0 && (
                <div className="font-mono text-[7px] opacity-70 leading-none mt-0.5" title={`${c.hist_games} historických zápasů`}>
                    {c.hist_ppg?.toFixed?.(1)}·{c.hist_games}g
                </div>
            )}
        </div>
    );
}

function PlayerFixtureSchedule({ schedule, loading, lang }) {
    const [colorMode, setColorMode] = React.useState("ppg"); // "ppg" | "dev"
    if (loading) {
        return (
            <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="schedule-loading">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 italic">
                    Načítám rozpis zápasů…
                </div>
            </div>
        );
    }
    if (!schedule || !schedule.cells) return null;
    // Group cells into phase rows for a nicer visual read.
    const PHASES = [
        { key: "gw1_6", label: "Start", lo: 1, hi: 6 },
        { key: "gw7_12", label: "1. třetina", lo: 7, hi: 12 },
        { key: "gw13_18", label: "Před Vánoci", lo: 13, hi: 18 },
        { key: "gw19_24", label: "Vánoce", lo: 19, hi: 24 },
        { key: "gw25_30", label: "Před finišem", lo: 25, hi: 30 },
        { key: "gw31_38", label: "Run-in", lo: 31, hi: 38 },
    ];
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3" data-testid="player-schedule">
            <div className="flex items-center gap-2 flex-wrap">
                <ArrowRight size={12} weight="fill" className="text-fa-sky" />
                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                    Rozpis zápasů 2026/27
                </h3>
                <span className="font-mono text-[9px] uppercase tracking-widest text-fa-sky border border-fa-sky/30 bg-fa-sky/[0.05] px-2 py-0.5">
                    Σ {schedule.total_expected?.toFixed(0)}p
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                    prům. {(schedule.total_expected / 38).toFixed(1)} PPG · dlouhodobý {schedule.overall_ppg?.toFixed(1)}
                </span>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    {/* Metric-in-cell toggle */}
                    <div className="flex border border-white/15" role="tablist" aria-label="Zobrazení hodnot">
                        <button
                            type="button"
                            onClick={() => setColorMode("ppg")}
                            data-testid="schedule-mode-ppg"
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${colorMode === "ppg" ? "bg-fa-yellow text-black font-bold" : "text-white/60 hover:text-white"}`}
                            title="Zobrazit očekávané body"
                        >
                            Body
                        </button>
                        <button
                            type="button"
                            onClick={() => setColorMode("dev")}
                            data-testid="schedule-mode-dev"
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border-l border-white/15 ${colorMode === "dev" ? "bg-fa-yellow text-black font-bold" : "text-white/60 hover:text-white"}`}
                            title="Zobrazit odchylku vs dlouhodobý průměr hráče"
                        >
                            Odchylka
                        </button>
                    </div>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                        <span className="text-fa-mint">H</span> doma · A venku · barva = <span className="text-fa-mint">nadprůměr</span> / <span className="text-orange-300">podprůměr</span>
                    </span>
                </div>
            </div>
            <div className="space-y-2">
                {PHASES.map((ph) => {
                    const cells = schedule.cells.filter((c) => c.gw >= ph.lo && c.gw <= ph.hi);
                    const phaseExp = schedule.phase_expected?.[ph.key];
                    return (
                        <div key={ph.key} className="space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50 min-w-[110px]">
                                    {ph.label} GW{ph.lo}-{ph.hi}
                                </span>
                                {phaseExp && (
                                    <>
                                        <span className="font-mono text-[9px] tabular-nums text-fa-yellow font-bold">
                                            {phaseExp.total?.toFixed(0)}p
                                        </span>
                                        <span className="font-mono text-[9px] tabular-nums text-white/50">
                                            avg {phaseExp.avg?.toFixed(1)} PPG
                                        </span>
                                        <span className={`font-mono text-[9px] tabular-nums ${phaseExp.deviation > 0 ? "text-fa-mint" : phaseExp.deviation < 0 ? "text-red-300" : "text-white/50"}`}>
                                            {phaseExp.deviation > 0 ? "+" : ""}{phaseExp.deviation?.toFixed(1)}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div className="grid grid-cols-6 gap-1">
                                {cells.map((c) => (
                                    <ScheduleCell key={c.gw} c={c} playerPpg={schedule.overall_ppg} mode={colorMode} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            {schedule.best_opponents?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-white/[0.08]">
                    <div className="border border-fa-mint/25 bg-fa-mint/[0.04] p-2">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint mb-1">
                            ↑ Nejlepší matchupy sezony
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {[...schedule.cells]
                                .filter((c) => !c.blank)
                                .sort((a, b) => b.expected_ppg - a.expected_ppg)
                                .slice(0, 5)
                                .map((c) => (
                                    <span key={c.gw} className="font-mono text-[10px] tabular-nums px-1.5 py-0.5 border border-fa-mint/40 text-fa-mint">
                                        GW{c.gw} vs {c.opponent}{c.is_home ? " H" : " A"} · {c.expected_ppg.toFixed(1)}
                                    </span>
                                ))}
                        </div>
                    </div>
                    <div className="border border-red-400/25 bg-red-500/[0.04] p-2">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-red-200 mb-1">
                            ↓ Nejtěžší matchupy sezony
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {[...schedule.cells]
                                .filter((c) => !c.blank)
                                .sort((a, b) => a.expected_ppg - b.expected_ppg)
                                .slice(0, 5)
                                .map((c) => (
                                    <span key={c.gw} className="font-mono text-[10px] tabular-nums px-1.5 py-0.5 border border-red-400/40 text-red-200">
                                        GW{c.gw} vs {c.opponent}{c.is_home ? " H" : " A"} · {c.expected_ppg.toFixed(1)}
                                    </span>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Opponent performance overview — Best/Worst historical matchups.
// Shows aggregated PPG per opponent team (min 2 games) plus a
// full sortable table.
// ---------------------------------------------------------------------------
function FixtureChip({ u }) {
    const label = `${u.is_home ? "HGW" : "AGW"}${u.gw}`;
    const phaseClr = u.phase_fit === "strong" ? "text-fa-mint border-fa-mint/50 bg-fa-mint/10"
                    : u.phase_fit === "weak" ? "text-red-300 border-red-400/50 bg-red-500/10"
                    : "text-white/60 border-white/20 bg-white/[0.03]";
    const opacity = u.finished ? "opacity-55" : "";
    return (
        <span
            title={`${u.is_home ? "Doma" : "Venku"} · GW${u.gw} · fáze ${u.phase} (${u.phase_fit})${u.finished ? " · odehráno" : ""}`}
            className={`inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${phaseClr} ${opacity} tabular-nums`}
        >
            {label}
            {u.finished && <span className="text-[8px] ml-0.5">✓</span>}
        </span>
    );
}

function OpponentRow({ o, accent }) {
    return (
        <li
            className="flex items-center justify-between font-mono text-[11px] gap-2 border-b border-white/[0.05] last:border-0 py-1.5"
            data-testid={`opp-row-${o.opponent.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
            <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                <div className="flex items-center gap-2">
                    <span className="font-display font-black text-[13px] truncate">{o.opponent}</span>
                    <span className="text-white/45 tabular-nums text-[10px]">{o.played}g</span>
                    <span className="text-white/60 tabular-nums text-[10px]">{o.points}p</span>
                    <span className={`tabular-nums font-bold text-[11px] ml-auto ${accent}`}>{o.ppg.toFixed(1)}</span>
                </div>
                {o.upcoming?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {o.upcoming.map((u, i) => <FixtureChip key={`${u.gw}-${i}`} u={u} />)}
                    </div>
                )}
            </div>
        </li>
    );
}

const FDR_BUCKET_STYLE = {
    1: { border: "border-emerald-400/60", bg: "bg-emerald-500/10", text: "text-fa-mint", label: "Nejlehčí" },
    2: { border: "border-emerald-400/40", bg: "bg-emerald-500/5", text: "text-fa-mint", label: "Lehcí" },
    3: { border: "border-yellow-400/50", bg: "bg-yellow-400/5", text: "text-fa-yellow", label: "Střední" },
    4: { border: "border-red-400/50", bg: "bg-red-500/5", text: "text-red-300", label: "Těžcí" },
    5: { border: "border-red-400/70", bg: "bg-red-500/10", text: "text-red-300", label: "Nejtěžší" },
};

function FdrAbilityStrip({ playerKey }) {
    const [data, setData] = React.useState(null);
    useEffect(() => {
        setData(null);
        if (!playerKey) return;
        timingFdrPerfDetail(playerKey).then(setData).catch(() => {});
    }, [playerKey]);
    if (!data || !(data.buckets || []).some((b) => b.games > 0)) return null;
    const tag = data.diff == null ? null
        : data.diff >= 1.0 ? { label: "Flat-track bully", cls: "bg-fa-yellow text-black", tip: "Výrazně lepší proti slabým soupeřům než proti silným" }
        : data.diff <= -0.5 ? { label: "Giant killer", cls: "bg-fa-mint text-black", tip: "Bodově se zvedá proti silným soupeřům" }
        : null;
    return (
        <div className="border-2 border-fa-mint/40 bg-gradient-to-b from-fa-mint/[0.06] to-transparent bg-[#0d0d0d] p-4 space-y-3" data-testid="fdr-ability-strip">
            <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display font-black text-base uppercase tracking-widest text-fa-mint">
                    ⚔ Výkon dle síly soupeře
                </h3>
                <span className="font-mono text-[10px] text-white/50 tabular-nums">Ø {data.ppg?.toFixed(2)} PPG · {data.games} zápasů</span>
                {data.diff != null && (
                    <span className={`font-mono text-[11px] font-bold tabular-nums ${data.diff < 0 ? "text-fa-mint" : "text-white/60"}`}
                          title="PPG vs slabí (FDR 1-2) minus PPG vs silní (FDR 4-5) — záporné = daří se proti silným">
                        L−T {data.diff > 0 ? "+" : ""}{data.diff?.toFixed(2)}
                    </span>
                )}
                {tag && (
                    <span className={`font-mono uppercase tracking-widest text-[10px] font-bold px-2 py-1 ${tag.cls}`} title={tag.tip} data-testid="fdr-ability-tag">
                        {tag.label}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="fdr-bucket-cards">
                {(data.buckets || []).map((b) => {
                    const st = FDR_BUCKET_STYLE[b.fdr] || FDR_BUCKET_STYLE[3];
                    const topOpp = (b.opponents || []).slice(0, 2);
                    return (
                        <div key={b.fdr} className={`border-2 ${b.games ? `${st.border} ${st.bg}` : "border-white/10"} p-3 space-y-1.5`}
                             data-testid={`fdr-bucket-${b.fdr}`}>
                            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-white/50">
                                <span className="font-bold">FDR {b.fdr} · {st.label}</span>
                                <span className="tabular-nums">{b.games || 0}z</span>
                            </div>
                            {b.games ? (
                                <>
                                    <div className={`font-display font-black text-3xl tabular-nums ${st.text}`}>
                                        {b.ppg?.toFixed(2)}<span className="font-mono text-[9px] text-white/40 ml-1">PPG</span>
                                    </div>
                                    <div className="font-mono text-[9px] text-white/60 flex gap-2.5 tabular-nums">
                                        <span title="Podíl zápasů s 10+ body">haul {b.haul_rate != null ? `${Math.round(b.haul_rate)}%` : "—"}</span>
                                        <span title="Podíl zápasů s ≤2 body">blank {b.blank_rate != null ? `${Math.round(b.blank_rate)}%` : "—"}</span>
                                        <span title="Bodové maximum">max {b.max_pts}</span>
                                    </div>
                                    {topOpp.length > 0 && (
                                        <div className="font-mono text-[9px] text-white/40 leading-relaxed">
                                            {topOpp.map((o) => `${o.opponent} ${o.ppg?.toFixed(1)}`).join(" · ")}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="font-mono text-[10px] text-white/25 py-2">bez zápasů</div>
                            )}
                        </div>
                    );
                })}
            </div>
            <p className="font-mono text-[8px] text-white/35">
                Historické PPG proti soupeřům dle jejich síly (FDR 1 = nejslabší, 5 = nejsilnější) · haul = 10+ bodů, blank = ≤2 body · pod kartou nejlepší soupeři v koši
            </p>
        </div>
    );
}

function OpponentOverview({ opponents, loading, lang }) {
    const [expanded, setExpanded] = React.useState(false);
    if (loading) {
        return (
            <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="opp-loading">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 italic">
                    Načítám matchupy…
                </div>
            </div>
        );
    }
    if (!opponents || (opponents.total_opponents ?? 0) === 0) return null;
    const { best = [], worst = [], opponents: all = [], player_team, phase_ppg, overall_ppg } = opponents;
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3" data-testid="opp-overview">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Sword size={12} weight="fill" className="text-fa-yellow" />
                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                    Přehled o soupeřích
                </h3>
                {player_team && (
                    <span className="font-mono text-[9px] uppercase tracking-widest text-fa-sky border border-fa-sky/30 bg-fa-sky/[0.05] px-2 py-0.5">
                        {player_team} · rozpis 2025-26
                    </span>
                )}
                <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40">
                    Historicky · min 2 zápasy · {opponents.total_opponents} soupeřů
                </span>
            </div>
            {/* Phase context strip */}
            {phase_ppg && overall_ppg > 0 && (
                <div className="flex items-center gap-1 flex-wrap font-mono text-[9px] uppercase tracking-widest border border-white/[0.08] bg-black/40 px-2 py-1.5"
                     data-testid="opp-phase-strip">
                    <span className="text-white/40 mr-1">Fáze × PPG:</span>
                    {Object.entries(phase_ppg).map(([k, v]) => {
                        const ratio = overall_ppg ? v / overall_ppg : 1;
                        const color = ratio >= 1.15 ? "text-fa-mint" : ratio <= 0.85 ? "text-red-300" : "text-white/70";
                        const label = k.replace("gw", "GW").replace("_", "-");
                        return (
                            <span key={k} className="flex items-center gap-1">
                                <span className="text-white/40">{label}</span>
                                <span className={`tabular-nums font-bold ${color}`}>{v.toFixed(1)}</span>
                            </span>
                        );
                    })}
                    <span className="text-white/45 ml-2">·</span>
                    <span className="text-white/40">průměr</span>
                    <span className="tabular-nums text-fa-yellow font-bold">{overall_ppg.toFixed(1)}</span>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Best matchups */}
                <div className="border border-fa-mint/30 bg-fa-mint/[0.03] p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <Trophy size={11} weight="fill" className="text-fa-mint" />
                        <h4 className="font-display font-black text-xs uppercase tracking-widest text-fa-mint">
                            Nejoblíbenější soupeři
                        </h4>
                    </div>
                    {best.length === 0 ? (
                        <div className="font-mono text-[10px] text-white/40 italic">Málo dat</div>
                    ) : (
                        <ul className="space-y-0" data-testid="opp-best-list">
                            {best.map((o) => <OpponentRow key={o.opponent} o={o} accent="text-fa-mint" />)}
                        </ul>
                    )}
                </div>
                {/* Worst matchups */}
                <div className="border border-red-400/30 bg-red-500/[0.03] p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendDown size={11} weight="fill" className="text-red-300" />
                        <h4 className="font-display font-black text-xs uppercase tracking-widest text-red-200">
                            Nejtěžší soupeři
                        </h4>
                    </div>
                    {worst.length === 0 ? (
                        <div className="font-mono text-[10px] text-white/40 italic">Málo dat</div>
                    ) : (
                        <ul className="space-y-0" data-testid="opp-worst-list">
                            {worst.map((o) => <OpponentRow key={o.opponent} o={o} accent="text-red-300" />)}
                        </ul>
                    )}
                </div>
            </div>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                data-testid="opp-toggle-all"
                className="font-mono text-[9px] uppercase tracking-widest text-fa-sky hover:text-fa-yellow"
            >
                {expanded ? "▲ Skrýt kompletní tabulku" : "▼ Zobrazit všechny soupeře"}
            </button>
            {expanded && (
                <div className="max-h-[320px] overflow-y-auto border border-white/10" data-testid="opp-full-table">
                    <table className="w-full font-mono text-[10px]">
                        <thead className="sticky top-0 bg-[#0d0d0d] border-b border-white/15">
                            <tr className="text-white/50 uppercase tracking-widest text-[9px]">
                                <th className="text-left px-2 py-1.5">Soupeř</th>
                                <th className="text-right px-2 py-1.5">Zápasy</th>
                                <th className="text-right px-2 py-1.5">Body</th>
                                <th className="text-right px-2 py-1.5">PPG</th>
                                <th className="text-right px-2 py-1.5">Doma</th>
                                <th className="text-right px-2 py-1.5">Venku</th>
                                <th className="text-right px-2 py-1.5">Best</th>
                                <th className="text-left px-2 py-1.5">Rozpis 25-26</th>
                            </tr>
                        </thead>
                        <tbody>
                            {all.map((o) => (
                                <tr key={o.opponent} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                                    <td className="px-2 py-1.5 font-display font-black text-[12px]">{o.opponent}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-white/50">{o.played}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-white/70">{o.points}</td>
                                    <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${o.ppg >= 6 ? "text-fa-mint" : o.ppg >= 3.5 ? "text-fa-yellow" : "text-red-300"}`}>
                                        {o.ppg.toFixed(1)}
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-white/50">
                                        {o.home_ppg != null ? `${o.home_ppg.toFixed(1)} (${o.home_played})` : "—"}
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-white/50">
                                        {o.away_ppg != null ? `${o.away_ppg.toFixed(1)} (${o.away_played})` : "—"}
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums text-fa-yellow">{o.best_haul}</td>
                                    <td className="px-2 py-1.5">
                                        {o.upcoming?.length ? (
                                            <div className="flex flex-wrap gap-1">
                                                {o.upcoming.map((u, i) => <FixtureChip key={`${u.gw}-${i}`} u={u} />)}
                                            </div>
                                        ) : (
                                            <span className="text-white/25">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Player Explorer — pick one player, see season vs average curve + phase mix.
// User can also add selected players to the shared Pool from here.
// ---------------------------------------------------------------------------
export default function PlayerExplorer({ lang, sharedPool, onSwitchTab, initialPlayerKey }) {
    const [query, setQuery] = useState("");
    const [posFilter, setPosFilter] = useState("");
    const [teamFilter, setTeamFilter] = useState("");
    const [teamOptions, setTeamOptions] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null); // player_key
    const [selectedMeta, setSelectedMeta] = useState(null);
    const [detail, setDetail] = useState(null);
    const [activeSeasons, setActiveSeasons] = useState([]); // toggled seasons
    const [chartMode, setChartMode] = useState("gw"); // "gw" | "phases"
    const [showAverage, setShowAverage] = useState(true);
    const [opponents, setOpponents] = useState(null);
    const [opponentsLoading, setOpponentsLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);        // {cells, overall_ppg, ...}
    const [scheduleLoading, setScheduleLoading] = useState(false);

    // Load the list of known teams once (from historical DB).
    useEffect(() => {
        timingKnownTeams()
            .then((d) => setTeamOptions(d.teams || []))
            .catch(() => setTeamOptions([]));
    }, []);

    // Deep-link: ?player={player_key} preselects a player on mount.
    useEffect(() => {
        if (initialPlayerKey) setSelected(initialPlayerKey);
    }, [initialPlayerKey]);

    // Debounced search
    useEffect(() => {
        const h = setTimeout(async () => {
            if (!query && !posFilter && !teamFilter) {
                setResults([]);
                return;
            }
            setLoading(true);
            try {
                const r = await timingSearchPlayers({
                    q: query || undefined,
                    position: posFilter || undefined,
                    team: teamFilter || undefined,
                    limit: 40,
                });
                setResults(r.players || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => clearTimeout(h);
    }, [query, posFilter, teamFilter]);

    // Keyboard navigation: ↑/↓ moves highlight, Enter selects the player.
    const { selectedIndex, onKeyDown: searchKeyDown, itemRef, resetIndex } = useListKeyboardNav({
        itemsLength: results.length,
        onEnter: (i) => {
            const p = results[i];
            if (!p) return;
            setSelected(p.player_key);
            setSelectedMeta(p);
        },
    });

    // Reset highlight whenever the query/filter changes.
    useEffect(() => { resetIndex(); }, [query, posFilter, teamFilter, resetIndex]);

    // Load detail on select
    useEffect(() => {
        if (!selected) {
            setDetail(null);
            setOpponents(null);
            setSchedule(null);
            return;
        }
        setLoading(true);
        timingPlayerDetail(selected)
            .then((d) => {
                setDetail(d);
                setActiveSeasons(Object.keys(d.seasons || {}));
            })
            .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
            .finally(() => setLoading(false));

        // Load opponent breakdown in parallel — non-blocking.
        setOpponentsLoading(true);
        setOpponents(null);
        setSchedule(null);
        timingPlayerOpponents(selected, { minGames: 2 })
            .then(setOpponents)
            .catch(() => setOpponents(null))
            .finally(() => setOpponentsLoading(false));
    }, [selected]);

    // Compute 38-GW fixture schedule for the selected player, reusing the
    // Fixture Matrix engine (which handles Sportmonks 26/27 fixtures + baseline
    // PPG blending for opponent × phase × H/A). Runs once `opponents` gives us
    // the resolved current-season team.
    useEffect(() => {
        if (!detail || !opponents?.player_team) {
            setSchedule(null);
            return;
        }
        const position = (detail.meta.positions && detail.meta.positions[0]) || "MID";
        const payload = [{
            fpl_id: null,
            historical_slug: detail.meta.player_key,
            display_name: detail.meta.display_name,
            position,
            team: opponents.player_team,
            current_price: (detail.meta.latest_price || 0) / 10.0,
            ownership_pct: null,
            availability_pct: 100,
        }];
        setScheduleLoading(true);
        timingPoolMatrix(payload)
            .then((d) => setSchedule(d.players?.[0] || null))
            .catch(() => setSchedule(null))
            .finally(() => setScheduleLoading(false));
    }, [detail, opponents?.player_team]);

    // Add the currently-selected player to the shared pool.
    const addSelectedToPool = async () => {
        if (!detail || !sharedPool) return;
        const slug = detail.meta.player_key;
        // We need to resolve the current FPL id + price for the pool entry.
        try {
            const r = await timingPoolMatch([{ query: detail.meta.display_name }]);
            const m = r.matches?.[0];
            if (!m || !m.current) {
                toast.error("Aktuální FPL hráč nenalezen — přidej ho ručně v Pool tabu");
                return;
            }
            const ok = sharedPool.addPlayer({
                fpl_id: m.current.fpl_id,
                display_name: m.current.display_name,
                historical_slug: m.historical_slug || slug,
                position: m.current.position,
                team: m.current.team,
                current_price: m.current.price,
                ownership_pct: m.current.ownership_pct,
                seasons_available: m.seasons_available || 0,
            });
            if (ok) toast.success(`${detail.meta.display_name} přidán do poolu`);
            else toast.info(sharedPool.pool.find((p) => p.historical_slug === slug) ? "Už je v poolu" : "Pool je plný");
        } catch (e) {
            toast.error("Přidání selhalo");
        }
    };

    const removeSelectedFromPool = () => {
        if (!detail || !sharedPool) return;
        const inPool = sharedPool.pool.find((p) => p.historical_slug === detail.meta.player_key);
        if (inPool) sharedPool.removePlayer(inPool.fpl_id);
    };

    const isSelectedInPool = detail && sharedPool
        ? sharedPool.pool.some((p) => p.historical_slug === detail.meta.player_key)
        : false;

    const chartData = useMemo(() => {
        if (!detail) return [];
        // one row per GW combining avg curve + each active season
        return Array.from({ length: 38 }, (_, i) => {
            const gw = i + 1;
            const row = { gw };
            const avgPoint = detail.average_curve?.[i];
            row["avg"] = avgPoint?.avg_points ?? null;
            for (const s of activeSeasons) {
                const series = detail.seasons?.[s]?.series || [];
                const hit = series.find((r) => r.gw === gw);
                row[s] = hit ? hit.points : null;
            }
            return row;
        });
    }, [detail, activeSeasons]);

    // Phase-mode chart data — average PPG per phase per season, plus overall avg.
    // Note: `avg` uses ALL player seasons (fixed reference) so it works even when
    // no season toggle is active — mirroring the GW-mode `average_curve` behaviour.
    const phaseChartData = useMemo(() => {
        if (!detail) return [];
        const allSeasons = Object.keys(detail.seasons || {});
        return PERIODS.map((p) => {
            const row = { phase: t(p.labelKey, lang).split(" ")[0] + ` ${p.gw[0]}-${p.gw[1]}` };
            // Per active season's PPG in this phase (from summary payload).
            for (const s of activeSeasons) {
                const timing = detail.seasons?.[s]?.timing?.[p.key];
                row[s] = timing?.ppg ?? null;
            }
            // Overall average across ALL of the player's seasons (not just active).
            const vals = allSeasons
                .map((s) => detail.seasons?.[s]?.timing?.[p.key]?.ppg)
                .filter((v) => v != null);
            row.avg = vals.length ? Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)) : null;
            return row;
        });
    }, [detail, activeSeasons, lang]);

    // Season color palette
    const seasonColor = (i) => [
        "#FFF3C2", "#9EE8B4", "#9DB7F5", "#F58BC0", "#F5A56B",
    ][i % 5];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="timing-explorer">
            {/* Left: search + list */}
            <aside className="border border-white/10 bg-[#0d0d0d] p-3 space-y-3" data-testid="timing-explorer-search">
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-fa-yellow">
                    <MagnifyingGlass size={11} weight="bold" /> {t("searchPlaceholder", lang).split("—")[0]}
                </div>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={searchKeyDown}
                    placeholder={t("searchPlaceholder", lang)}
                    data-testid="timing-search-input"
                    className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-yellow"
                />
                <div className="flex gap-1 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setPosFilter("")}
                        data-testid="timing-pos-all"
                        className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${!posFilter ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                    >
                        {t("allPositions", lang)}
                    </button>
                    {POSITIONS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPosFilter(p)}
                            data-testid={`timing-pos-${p}`}
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${posFilter === p ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-1">
                    <select
                        value={teamFilter}
                        onChange={(e) => setTeamFilter(e.target.value)}
                        data-testid="timing-team-filter"
                        className={`bg-black/40 border px-2 py-1 font-mono text-[10px] focus:outline-none focus:border-fa-yellow flex-1 ${teamFilter ? "border-fa-yellow text-fa-yellow font-bold" : "border-white/15 text-white/60"}`}
                        title="Filtruj hráče podle týmu (napříč historií)"
                    >
                        <option value="">{lang === "cs" ? "Všechny týmy" : "All teams"}</option>
                        {teamOptions.map((tm) => (
                            <option key={tm} value={tm}>{tm}</option>
                        ))}
                    </select>
                    {teamFilter && (
                        <button
                            type="button"
                            onClick={() => setTeamFilter("")}
                            data-testid="timing-team-clear"
                            aria-label="Zrušit filtr týmu"
                            className="font-mono text-[10px] text-white/50 border border-white/15 px-2 py-1 hover:text-white hover:border-white/40"
                        >
                            ✕
                        </button>
                    )}
                </div>
                <div className="max-h-[65vh] overflow-y-auto -mx-3 px-3 space-y-1">
                    {loading && <div className="font-mono text-[10px] text-white/40 italic">{t("loading", lang)}</div>}
                    {!loading && results.length === 0 && (
                        <div className="font-mono text-[10px] text-white/40 italic">{t("noData", lang)}</div>
                    )}
                    {results.map((p, i) => (
                        <button
                            key={p.player_key}
                            type="button"
                            ref={itemRef(i)}
                            onClick={() => {
                                setSelected(p.player_key);
                                setSelectedMeta(p);
                            }}
                            data-testid={`timing-pick-${p.player_key.replace(/[^a-z0-9]+/gi, "-")}`}
                            className={`w-full text-left border px-2 py-1.5 transition ${selected === p.player_key ? "border-fa-yellow bg-fa-yellow/5" : i === selectedIndex ? "border-fa-yellow/60 bg-fa-yellow/[0.04]" : "border-white/10 hover:border-white/30 bg-white/[0.02]"}`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-display font-black text-[13px] truncate">
                                    {p.display_name}
                                </span>
                                <PositionPill positions={p.positions} />
                            </div>
                            <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 flex items-center justify-between">
                                <span>
                                    {(p.seasons || []).length}× {t("seasons", lang).toLowerCase()}
                                    {p.latest_team && <span className="ml-1 text-white/60">· {p.latest_team}</span>}
                                </span>
                                {p.latest_price > 0 && (
                                    <span className="text-fa-yellow tabular-nums">£{(p.latest_price / 10).toFixed(1)}m</span>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </aside>

            {/* Right: detail */}
            <section className="space-y-4">
                {!detail && (
                    <div className="border-2 border-dashed border-white/15 bg-[#0d0d0d] p-8 text-center">
                        <ChartLine size={28} weight="bold" className="mx-auto text-white/25 mb-2" />
                        <p className="font-mono text-[11px] text-white/50">{t("pickPlayer", lang)}</p>
                    </div>
                )}
                {detail && (
                    <>
                        {/* Header */}
                        <header className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="timing-explorer-header">
                            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                <div>
                                    <h2 className="font-display font-black text-2xl uppercase tracking-tight">
                                        {detail.meta.display_name}
                                    </h2>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-0.5 flex items-center gap-2 flex-wrap">
                                        <span>{(detail.meta.positions || []).join(", ")} · {(detail.meta.seasons || []).length}× {t("seasons", lang).toLowerCase()}</span>
                                        <Link
                                            to={`/players/${encodeURIComponent(detail.meta.player_key)}`}
                                            data-testid="explorer-open-profile"
                                            className="text-fa-mint border border-fa-mint/40 px-2 py-0.5 hover:bg-fa-mint hover:text-black transition-colors flex items-center gap-1 font-bold"
                                        >
                                            <ArrowSquareOut size={10} weight="bold" /> Otevřít profil
                                        </Link>
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <MetricPill label={t("totalPoints", lang)} value={detail.aggregate.total_points} />
                                    <MetricPill label={t("gamesPlayed", lang)} value={detail.aggregate.games_played} />
                                    <MetricPill
                                        label={t("consistency", lang)}
                                        value={detail.aggregate.consistency ?? "—"}
                                        accent="mint"
                                        rank={detail.ranks?.consistency}
                                    />
                                    <MetricPill
                                        label={t("explosiveness", lang)}
                                        value={detail.aggregate.explosiveness ?? "—"}
                                        accent="yellow"
                                        rank={detail.ranks?.explosiveness}
                                    />
                                    <MetricPill
                                        label={t("value", lang)}
                                        value={detail.aggregate.value ?? "—"}
                                        accent="sky"
                                        rank={detail.ranks?.value}
                                    />
                                </div>
                            </div>
                            {/* Pool actions */}
                            {sharedPool && (
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10 flex-wrap">
                                    {isSelectedInPool ? (
                                        <>
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                                                <Check size={11} weight="bold" /> V poolu
                                            </span>
                                            <button
                                                type="button"
                                                onClick={removeSelectedFromPool}
                                                data-testid="explorer-remove-from-pool"
                                                className="font-mono text-[10px] uppercase tracking-widest text-red-300 border border-red-300/40 px-2 py-1 hover:bg-red-300 hover:text-black"
                                            >
                                                Odebrat z poolu
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={addSelectedToPool}
                                            data-testid="explorer-add-to-pool"
                                            className="font-mono text-[10px] uppercase tracking-widest bg-fa-sky text-black px-3 py-1.5 hover:bg-fa-sky/80 font-bold flex items-center gap-1.5"
                                        >
                                            <Plus size={11} weight="bold" /> Přidat do poolu
                                        </button>
                                    )}
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/45 ml-auto">
                                        Pool: <strong className="text-white">{sharedPool.pool.length}</strong>/30 · £{sharedPool.totalPrice.toFixed(1)}m
                                    </span>
                                    {onSwitchTab && sharedPool.pool.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => onSwitchTab("pool")}
                                            data-testid="explorer-goto-pool"
                                            className="font-mono text-[10px] uppercase tracking-widest text-fa-sky hover:text-fa-yellow flex items-center gap-1"
                                        >
                                            Otevřít Pool <ArrowRight size={10} weight="bold" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </header>

                        {/* Season picker + Average toggle */}
                        <div className="flex items-stretch gap-2 flex-wrap">
                            <div className="flex-1 min-w-[280px]">
                                <SeasonPicker
                                    seasons={Object.keys(detail.seasons || {})}
                                    active={activeSeasons}
                                    onToggle={(s) =>
                                        setActiveSeasons((cur) =>
                                            cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]
                                        )
                                    }
                                    lang={lang}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAverage((v) => !v)}
                                data-testid="timing-avg-toggle"
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 border ${showAverage ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:text-white"}`}
                            >
                                {t("averageCurve", lang)}
                                <span className={`ml-2 ${showAverage ? "text-black/60" : "text-white/40"}`}>
                                    {showAverage ? "✓" : "○"}
                                </span>
                            </button>
                        </div>

                        {/* Chart */}
                        <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="timing-chart">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                                    {chartMode === "gw" ? t("pointsCurve", lang) : t("timingBreakdown", lang)}
                                </h3>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setChartMode("gw")}
                                        data-testid="timing-mode-gw"
                                        className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 flex items-center gap-1 border ${chartMode === "gw" ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                                    >
                                        <ChartLine size={10} weight={chartMode === "gw" ? "fill" : "regular"} />
                                        GW
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setChartMode("phases")}
                                        data-testid="timing-mode-phases"
                                        className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 flex items-center gap-1 border ${chartMode === "phases" ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/15 text-white/60 hover:text-white"}`}
                                    >
                                        <ChartBar size={10} weight={chartMode === "phases" ? "fill" : "regular"} />
                                        Fáze
                                    </button>
                                </div>
                            </div>
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    {chartMode === "gw" ? (
                                        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                            <XAxis
                                                dataKey="gw"
                                                stroke="rgba(255,255,255,0.4)"
                                                tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                                            />
                                            <YAxis
                                                stroke="rgba(255,255,255,0.4)"
                                                tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                                            />
                                            <Tooltip
                                                wrapperStyle={{ outline: "none" }}
                                                contentStyle={{
                                                    background: "#0d0d0d",
                                                    border: "1px solid rgba(255,255,255,0.15)",
                                                    fontFamily: "IBM Plex Mono, monospace",
                                                    fontSize: 11,
                                                    color: "#fff",
                                                }}
                                                labelStyle={{ color: "#FFF3C2", fontWeight: 700 }}
                                                itemStyle={{ color: "#fff" }}
                                                cursor={{ stroke: "rgba(255,255,255,0.25)", strokeWidth: 1 }}
                                                labelFormatter={(gw) => `GW ${gw}`}
                                            />
                                            <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }} />
                                            {showAverage && (
                                                <Line
                                                    type="monotone"
                                                    dataKey="avg"
                                                    name={t("averageCurve", lang)}
                                                    stroke="rgba(255,255,255,0.55)"
                                                    strokeDasharray="6 3"
                                                    strokeWidth={2}
                                                    dot={false}
                                                    connectNulls
                                                />
                                            )}
                                            {activeSeasons.map((s, i) => (
                                                <Line
                                                    key={s}
                                                    type="monotone"
                                                    dataKey={s}
                                                    name={s}
                                                    stroke={seasonColor(i)}
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    connectNulls
                                                />
                                            ))}
                                        </LineChart>
                                    ) : (
                                        <BarChart data={phaseChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                            <XAxis
                                                dataKey="phase"
                                                stroke="rgba(255,255,255,0.4)"
                                                tick={{ fontSize: 9, fontFamily: "IBM Plex Mono, monospace" }}
                                            />
                                            <YAxis
                                                stroke="rgba(255,255,255,0.4)"
                                                tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                                                label={{ value: "PPG", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                                            />
                                            <Tooltip
                                                wrapperStyle={{ outline: "none" }}
                                                contentStyle={{
                                                    background: "#0d0d0d",
                                                    border: "1px solid rgba(255,255,255,0.15)",
                                                    fontFamily: "IBM Plex Mono, monospace",
                                                    fontSize: 11,
                                                    color: "#fff",
                                                }}
                                                labelStyle={{ color: "#FFF3C2", fontWeight: 700 }}
                                                itemStyle={{ color: "#fff" }}
                                                cursor={{ fill: "rgba(255,255,255,0.05)" }}
                                            />
                                            <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }} />
                                            {activeSeasons.map((s, i) => (
                                                <Bar
                                                    key={s}
                                                    dataKey={s}
                                                    name={s}
                                                    fill={seasonColor(i)}
                                                    radius={[2, 2, 0, 0]}
                                                />
                                            ))}
                                            {showAverage && (
                                                <Bar
                                                    dataKey="avg"
                                                    name={t("averageCurve", lang)}
                                                    fill="rgba(255,255,255,0.35)"
                                                    radius={[2, 2, 0, 0]}
                                                />
                                            )}
                                        </BarChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Season summary grid — hide seasons where the player didn't play. */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="timing-season-cards">
                            {Object.entries(detail.seasons)
                                .filter(([, s]) => (s.games_played || 0) > 0 || (s.total_points || 0) > 0)
                                .map(([season, s]) => (
                                <div key={season} className="border border-white/10 bg-[#0d0d0d] p-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-display font-black text-base">{season}</span>
                                        <span className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow tabular-nums">
                                            {s.total_points}p
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-1 font-mono text-[9px] uppercase tracking-widest text-white/60">
                                        <div>
                                            <div className="text-white/40">{t("consistency", lang).slice(0, 4)}</div>
                                            <div className="text-fa-mint tabular-nums">{s.consistency ?? "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-white/40">{t("explosiveness", lang).slice(0, 4)}</div>
                                            <div className="text-fa-yellow tabular-nums">{s.explosiveness ?? "—"}%</div>
                                        </div>
                                        <div>
                                            <div className="text-white/40">{t("value", lang).slice(0, 4)}</div>
                                            <div className="text-fa-sky tabular-nums">{s.value ?? "—"}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* PPG vs opponent strength (FDR buckets) — prominent, right after season cards */}
                        <FdrAbilityStrip playerKey={selected} />

                        {/* Opponent performance overview — best/worst historical matchups */}
                        <OpponentOverview
                            opponents={opponents}
                            loading={opponentsLoading}
                            lang={lang}
                        />

                        {/* Full 38-GW fixture schedule (with H/A + expected PPG per opponent) */}
                        <PlayerFixtureSchedule
                            schedule={schedule}
                            loading={scheduleLoading}
                            lang={lang}
                        />

                        {/* Timing breakdown */}
                        <TimingBreakdown timing={detail.aggregate.timing} lang={lang} />
                    </>
                )}
            </section>
        </div>
    );
}
